import React, { useEffect, useState } from 'react';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';
import { ErrorLayout } from './ErrorLayout';
import { Button } from '../common/Button';
import { Alert } from '../common/Alert';

/**
 * OfflinePage Component
 * 
 * Displays when user loses internet connection.
 * Automatically dismisses when connection is restored.
 * 
 * Features:
 * - Real-time connection monitoring
 * - Auto-dismiss on reconnection
 * - Manual retry option
 * - Success alert on reconnection
 * - Dark mode compatible
 * - Mobile-first responsive
 * 
 * Industry Pattern: Google offline page, GitHub offline
 * 
 * @component
 * @example
 * ```tsx
 * // In App.tsx with useOnlineStatus hook
 * const isOnline = useOnlineStatus();
 * if (!isOnline) {
 *   return <OfflinePage />;
 * }
 * ```
 * 
 * @returns {JSX.Element} Rendered offline error page
 */
export const OfflinePage: React.FC = () => {
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-reload after 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleRetry = () => {
    setIsReconnecting(true);
    // Check connection after 1 second
    setTimeout(() => {
      if (navigator.onLine) {
        window.location.reload();
      } else {
        setIsReconnecting(false);
      }
    }, 1000);
  };

  return (
    <ErrorLayout
      title="No Internet Connection"
      description="Please check your internet connection and try again."
      icon={<WifiOff className="w-16 h-16" />}
      actions={
        <Button 
          variant="primary" 
          size="md"
          onClick={handleRetry}
          loading={isReconnecting}
          icon={<RefreshCw className="w-4 h-4" />}
          iconPosition="left"
          fullWidth
        >
          {isReconnecting ? 'Checking...' : 'Retry Connection'}
        </Button>
      }
    >
      {isOnline && (
        <Alert
          variant="success"
          title="Connection Restored!"
          description="Reloading page..."
          icon={<Wifi />}
        />
      )}
      
      <div className="text-xs text-gray-500 dark:text-slate-500 mt-4">
        <p>Tips:</p>
        <ul className="list-disc list-inside text-left space-y-1 mt-2">
          <li>Check your WiFi or cellular connection</li>
          <li>Make sure airplane mode is off</li>
          <li>Try refreshing the page</li>
        </ul>
      </div>
    </ErrorLayout>
  );
};

